import React, { useState, useEffect } from 'react';
import LandingPage from './components/LandingPage';
import WaitlistModal from './components/WaitlistModal';
import SymptomLogger from './components/SymptomLogger';
import TrackerGate from './components/TrackerGate';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import GetTheApp from './pages/GetTheApp';
import { supabase, signOut } from './lib/supabaseClient';
import './App.css';

function getInitialPage() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  if (path.startsWith('/blog/')) {
    const slug = path.replace('/blog/', '').replace(/\/$/, '');
    return { page: 'blogpost', slug };
  }
  if (path === '/blog') {
    return { page: 'blog', slug: null };
  }
  if (path === '/get-the-app') {
    return { page: 'gettheapp', slug: null };
  }
  if (params.get('track') === 'true') {
    return { page: 'track', slug: null };
  }
  return { page: 'landing', slug: null };
}

function App() {
  const initial = getInitialPage();
  const [currentPage, setCurrentPage] = useState(initial.page === 'track' ? 'checking' : initial.page);
  const [blogSlug, setBlogSlug] = useState(initial.slug);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [journeyStage, setJourneyStage] = useState(localStorage.getItem('apptnote_journey_stage') || null);

  // Resume a signed-in session if there is one. A session only counts if the
  // user also completed sign-in on this device (consent recorded), which is
  // marked by 'apptnote_profile_ok'. Sessions from the old email-only sign-in
  // are discarded, so everyone signs in once with a code.
  const resumeSession = async () => {
    try { localStorage.removeItem('apptnote_session_token'); } catch (e) { /* ignore */ }
    if (!supabase) return false;
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    const email = session?.user?.email?.toLowerCase();
    let profileOk = null;
    try { profileOk = localStorage.getItem('apptnote_profile_ok'); } catch (e) { /* ignore */ }
    if (session && email && profileOk === email) {
      setUserEmail(email);
      setSessionToken(session.access_token);
      return true;
    }
    if (session) await signOut();
    return false;
  };

  // Handle ?track=true on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('track') === 'true') {
      resumeSession().then((ok) => setCurrentPage(ok ? 'tracker' : 'gate'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the access token fresh. Supabase refreshes it automatically before it
  // expires; this passes the new one down to every component.
  useEffect(() => {
    if (!supabase) return undefined;
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setSessionToken(null);
      } else {
        setSessionToken(session.access_token);
      }
    });
    return () => data?.subscription?.unsubscribe();
  }, []);

  // Keep URL in sync with page state
  useEffect(() => {
    if (currentPage === 'blog') {
      window.history.pushState({}, '', '/blog');
    } else if (currentPage === 'blogpost' && blogSlug) {
      window.history.pushState({}, '', `/blog/${blogSlug}`);
    } else if (currentPage === 'gettheapp') {
      window.history.pushState({}, '', '/get-the-app');
    } else if (currentPage === 'landing') {
      window.history.pushState({}, '', '/');
    }
  }, [currentPage, blogSlug]);

  const handleEmailSubmit = (email, token, journey_stage) => {
    setUserEmail(email);
    setSessionToken(token);
    if (journey_stage) setJourneyStage(journey_stage);
    setCurrentPage('tracker');
  };

  const handleStartTracking = async () => {
    const ok = await resumeSession();
    setCurrentPage(ok ? 'tracker' : 'gate');
  };

  const handleTrackerClose = () => {
    window.history.pushState({}, '', '/');
    setCurrentPage('landing');
  };

  const handleLogout = async () => {
    await signOut();
    try { localStorage.removeItem('apptnote_profile_ok'); } catch (e) { /* ignore */ }
    setUserEmail(null);
    setSessionToken(null);
    setCurrentPage('gate');
  };

  const handleOpenBlog = () => setCurrentPage('blog');

  const handleOpenPost = (slug) => {
    setBlogSlug(slug);
    setCurrentPage('blogpost');
  };

  const handleBackToLanding = () => {
    window.history.pushState({}, '', '/');
    setCurrentPage('landing');
  };

  const handleGetTheApp = () => setCurrentPage('gettheapp');

  return (
    <div className="App">
      {currentPage === 'landing' && (
        <LandingPage
          onJoinWaitlist={() => setShowWaitlist(true)}
          onStartTracking={handleStartTracking}
          onBlog={handleOpenBlog}
          onGetTheApp={handleGetTheApp}
        />
      )}
      {currentPage === 'gate' && (
        <TrackerGate
          onEmailSubmit={handleEmailSubmit}
          onJoinWaitlist={() => {
            setCurrentPage('landing');
            setShowWaitlist(true);
          }}
        />
      )}
      {currentPage === 'tracker' && userEmail && sessionToken && (
        <SymptomLogger
          userEmail={userEmail}
          sessionToken={sessionToken}
          journeyStage={journeyStage}
          onClose={handleTrackerClose}
          onLogout={handleLogout}
          onGetTheApp={handleGetTheApp}
        />
      )}
      {currentPage === 'blog' && (
        <Blog
          onBack={handleBackToLanding}
          onPost={handleOpenPost}
          onStartTracking={handleStartTracking}
        />
      )}
      {currentPage === 'blogpost' && blogSlug && (
        <BlogPost
          slug={blogSlug}
          onBlog={handleOpenBlog}
          onBack={handleBackToLanding}
          onStartTracking={handleStartTracking}
        />
      )}
      {currentPage === 'gettheapp' && (
        <GetTheApp
          onBack={handleBackToLanding}
          onStartTracking={handleStartTracking}
        />
      )}
      {showWaitlist && (
        <WaitlistModal onClose={() => setShowWaitlist(false)} context="pro" />
      )}
    </div>
  );
}

export default App;